import React, { useMemo, useState } from "react";
import "./IndicatorsInfoTable.css";
import { useSuburbIndicatorsTable } from "../../../hooks/useSuburbIndicatorsTable";

const fmtCurrency = (v) =>
  typeof v === "number"
    ? new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: "AUD",
        maximumFractionDigits: 0,
      }).format(v)
    : "—";

const fmtPct = (v) =>
  typeof v === "number" ? `${Number.isInteger(v) ? v : v.toFixed(2)}%` : "—";

function SortIcon({ active, direction }) {
  return (
    <svg
      className={`sort-main-icon${active ? " sort-icon-active" : ""}`}
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M6 11L9 14L12 11"
        stroke={active && direction === "desc" ? "#3857e0" : "#B1B1B1"}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 7L9 4L6 7"
        stroke={active && direction === "asc" ? "#3857e0" : "#B1B1B1"}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function GrowthPill({ value }) {
  const positive = typeof value === "number" && value >= 0;
  return (
    <span
      className={`growth-pill-modern ${positive ? "positive" : "negative"}`}
      title={fmtPct(value)}
    >
      {positive ? (
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
          <path
            d="M8 13V3M8 3l-3 3M8 3l3 3"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
          <path
            d="M8 3v10M8 13l-3-3M8 13l3-3"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {fmtPct(value)}
    </span>
  );
}

const FILTER_MAP = {
  median_sales_price_last_12_months: { field: "median12m",         type: "currency" },
  median_price_change_5y_pct:        { field: "pct5y",             type: "percent"  },
  median_price_change_10y_pct:       { field: "pct10y",            type: "percent"  },
  lt_growth_rate_20y_pct:            { field: "ltgrowth20ypct",    type: "percent"  },
  rental_yield_pct:                  { field: "rentalyield12mpct", type: "percent"  },
};

function inRange(tableValue, low, high, type) {
  if (typeof tableValue !== "number" || typeof low !== "number" || typeof high !== "number") {
    return true;
  }
  return tableValue >= low && tableValue <= high;
}

/* ✅ AQUÍ VA EL CAMBIO IMPORTANTE:
     envolvemos TODA la tabla en un div capturable */
export default function IndicatorsInfoTable({
  city,
  selectedSuburbs = [],
  rangeFilters = {},
  onSelect,
  selectedSuburb,
  isActiveMember = false,
}) {
  return (
    <div className="indicators-table-container">
      <IndicatorsInfoTableContent
        city={city}
        selectedSuburbs={selectedSuburbs}
        rangeFilters={rangeFilters}
        onSelect={onSelect}
        selectedSuburb={selectedSuburb}
        isActiveMember={isActiveMember}
      />
    </div>
  );
}

/* ====== CONTENIDO ORIGINAL SIN CAMBIOS ====== */
function IndicatorsInfoTableContent({
  city,
  selectedSuburbs = [],
  rangeFilters = {},
  onSelect,
  selectedSuburb,
  isActiveMember = false,
}) {
  const { data, loading, error } = useSuburbIndicatorsTable(city);
  const [activeSuburb, setActiveSuburb] = useState(null);

  const filtered = useMemo(() => {
    let rows = Array.isArray(data) ? data : [];

    if (selectedSuburbs && selectedSuburbs.length > 0) {
      const set = new Set(selectedSuburbs.map((s) => String(s).trim().toLowerCase()));
      rows = rows.filter((r) => set.has(String(r?.suburb ?? "").toLowerCase()));
    }

    const entries = Object.entries(rangeFilters || {});
    if (entries.length > 0) {
      rows = rows.filter((row) =>
        entries.every(([rangeKey, cfg]) => {
          const map = FILTER_MAP[rangeKey];
          if (!map) return true;
          const { field, type } = map;
          const { low, high } = cfg || {};
          if (typeof low !== "number" || typeof high !== "number") return true;
          return inRange(row?.[field], low, high, type);
        })
      );
    }

    return rows;
  }, [data, selectedSuburbs, rangeFilters]);

  const columns = useMemo(
    () => [
      { key: "suburb", label: "Suburb", type: "text" },
      { key: "median12m", label: "Median sales price\nlast 12 months", type: "money" },
      { key: "median5y", label: "5 years \nmedian", type: "money" },
      { key: "median10y", label: "10 years \nmedian", type: "money" },
      { key: "median20y", label: "20 years \nmedian", type: "money" },
      { key: "ltgrowth20ypct", label: "LT growth rate (%) 20 years", type: "pill" },
      { key: "pct5y", label: "5 years median % change", type: "pill" },
      { key: "pct10y", label: "10 years median % change", type: "pill" },
      { key: "pct20y", label: "20 years median % change", type: "pill" },
      { key: "rentalyield12mpct", label: "Median rental yield (12 months)", type: "pill" },
    ],
    []
  );

  const [sort, setSort] = useState({ key: "suburb", dir: "asc" });
  const sorted = useMemo(() => {
    const arr = [...(filtered || [])];
    arr.sort((a, b) => {
      const { key, dir } = sort;
      const va = a?.[key];
      const vb = b?.[key];
      const mul = dir === "asc" ? 1 : -1;

      if (typeof va === "string" || typeof vb === "string") {
        return mul * String(va ?? "").localeCompare(String(vb ?? ""), "en");
      }
      const na = Number.isFinite(va) ? va : Number.NEGATIVE_INFINITY;
      const nb = Number.isFinite(vb) ? vb : Number.NEGATIVE_INFINITY;
      return mul * (na - nb);
    });
    
    // Limitar a 1 fila si no es miembro activo
    if (!isActiveMember && arr.length > 0) {
      return arr.slice(0, 1);
    }
    
    return arr;
  }, [filtered, sort, isActiveMember]);

  const toggleSort = (key) => {
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    );
  };

  const currentSelected = selectedSuburb ?? activeSuburb;

  const handleRowClick = (row) => {
    const suburb = row?.suburb;
    if (!suburb) return;
    setActiveSuburb(suburb);
    if (onSelect) onSelect({ suburb, city });
  };

  const handleRowKey = (e, row) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleRowClick(row);
    }
  };

  return (
    <div className="ind-table__wrapper">
      <div className="ind-table__header">
        <h3>Suburbs indicators in {city || "—"}</h3>
      </div>

      {error && <div className="ind-table__state ind-table__state--error">Error loading table</div>}
      {loading && <div className="ind-table__state">Loading…</div>}

      {!loading && !error && (
        <div className="ind-table__scroller">
          <table className="ind-table">
            <colgroup>
              <col className="col-suburb" />
              <col className="col-med12" />
              <col className="col-med5" />
              <col className="col-med10" />
              <col className="col-med20" />
              <col className="col-lt" />
              <col className="col-pct5" />
              <col className="col-pct10" />
              <col className="col-pct20" />
              <col className="col-rent" />
            </colgroup>

            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className="ind-table__th">
                    <button
                      type="button"
                      className="th__sortbtn"
                      onClick={() => toggleSort(c.key)}
                      aria-label={`Sort by ${c.label.replace(/\n/g, " ")}`}
                    >
                      <div className="header-content">
                        <span className="th__label">
                          {c.label.split("\n").map((l, i) => (
                            <span key={i} className="th__line">{l}</span>
                          ))}
                        </span>
                        <SortIcon active={sort.key === c.key} direction={sort.dir} />
                      </div>
                    </button>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {sorted.map((row, idx) => {
                const isSelected = String(currentSelected ?? "") === String(row.suburb ?? "");
                return (
                  <tr
                    key={`${row.suburb}-${idx}`}
                    className={`ind-table__tr ind-table__tr--click${
                      isSelected ? " ind-table__tr--selected" : ""
                    }`}
                    tabIndex={0}
                    onClick={() => handleRowClick(row)}
                    onKeyDown={(e) => handleRowKey(e, row)}
                    aria-selected={isSelected}
                  >
                    {columns.map((c) => {
                      const v = row[c.key];
                      let cell;
                      if (c.type === "money") cell = fmtCurrency(v);
                      else if (c.type === "pill") cell = <GrowthPill value={v} />;
                      else cell = v ?? "—";
                      return (
                        <td
                          key={c.key}
                          className={`ind-table__td ${
                            c.key === "suburb" ? "ind-table__td--sticky" : ""
                          }`}
                        >
                          {cell}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
 